import {
  Box,
  Button,
  Checkbox,
  FormControl,
  TextField,
  Container,
  Grid,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import SectionCard from "../../ui/forms/SectionCard";
import { CreateCandidateValidationSchema } from "../../utils/Validations";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  postRequest,
  getRequestWithToken,
  getRequest,
  putRequestWithToken,
  getRequestWithTokenV3,
} from "../../utils/ApiRequests";
import { useContext, useEffect, useRef, useState } from "react";
import "./candidates.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { GlobalContext } from "../../components/GlobalStateProvide/GlobalStateProvider";
import FocusError from "../../components/FocusError/FocusError";
import ReusableLoader from "../../components/ReusableLoader/ReusableLoader";
import BackBtn from "../../components/backButton/BackBtn";
import { checkPermission } from "../../utils/userPermissions";
import Unauthorized from "../../components/unauthorized/Unauthorized";
import MobileNumberInput from "./MobileNumberInput";
import OTPVerification from "./OTPVerification";
import CandidateRoleSelectionPanel from "./CandidateRoleSelectionPanel";

const CreateCandidates = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);

  const [dataById, setDataById] = useState([]);
  const location = useLocation();
  const [showLoader, setShowLoader] = useState(true);

  // OTP Verification States
  const [verificationStep, setVerificationStep] = useState('mobile'); // 'mobile', 'otp', 'form'
  const [verifiedMobileNumber, setVerifiedMobileNumber] = useState('');

  const [canCreate, setCanCreate] = useState(true);
  const [canDelete, setCanDelete] = useState(true);
  const [canUpdate, setCanUpdate] = useState(true);
  const [canRead, setCanRead] = useState(true);
  const [isAccess, setisAccess] = useState([]);
  const [isIncludesValue, setisIncludesValue] = useState(true);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [roleSelectionState, setRoleSelectionState] = useState({ mappingId: "", familyId: "", groupId: "", roleId: "", initialMappingId: "", isChanged: false });
  const [roleSelectionError, setRoleSelectionError] = useState("");

  const [initialValues, setInitialValues] = useState({
    user_name: "",
    mobile_number: "",
    email: "",
    dob: null,
    gender: "",
    pincode: "",
    state: "",
    district: "",
    city: "",
    current_employment_status: "",
    past_job_count: 0,
    past_work_experience: "",
    english_proficiency: "",
    term_and_conditions: true,
    documents_owned: {},
    assets_owned: {},
    work_mode_choice: {},
    education_information: [],
    certification: [],
    engagement_type: "",
    expectedSalary: "",
    joining_availability: "",
    shift_preferences: [],
    migrate_concern: false,
    language: []
  });
  const [editForm, setEditForm] = useState(false);

  const { handleSnackBar } = useContext(GlobalContext);

  const scrollTargetRef = useRef(null);
  const navigate = useNavigate();

  const experienceFormat = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are 0-based
    const day = date.getDate().toString().padStart(2, "0");
    return `${day}/${month}/${year}`;
  };

  const formatDateOfBirth = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are 0-based
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const resolveCandidateIdFromResponse = async (response, mobileNumber) => {
    const responseData = response?.data?.data;
    const directId = responseData?._id || responseData?.candidate?._id || responseData?.user?._id || response?.data?._id;
    if (directId) return directId;

    const lookupResponse = await getRequestWithToken(`admin/candidate/${mobileNumber}`);
    return lookupResponse?.data?.data?._id || "";
  };

  const markAdminOnboardingComplete = async (mobileNumber) => {
    await putRequestWithToken(`admin/candidate/update/${mobileNumber}`, { reg_form_status: "5" });
  };

  const buildKnownLanguagesPayload = (languages = []) => languages
    .map((lang) => {
      const value = Number(lang.value ?? lang.id);
      if (Number.isNaN(value)) return null;
      return {
        value,
        speak: Boolean(lang.speak),
        read: Boolean(lang.read),
        write: Boolean(lang.write),
      };
    })
    .filter(Boolean);

  const handleSubmit = async (values, { resetForm }) => {
    // Convert education_information array to simple qualification array
    setFormSubmitted(true);

    if (!roleSelectionState.mappingId) {
      setFormSubmitted(false);
      setRoleSelectionError("Family, group, and role are required.");
      handleSnackBar(true, "Please select candidate family, group and role.", "error");
      return;
    }
    setRoleSelectionError("");

    const educationPayload = values.education_information.map(item => ({
      qualification: item.qualification
    }));

    // Filter out false values from documents_owned
    const cleanedDocumentsOwned = Object.keys(values.documents_owned).reduce((acc, key) => {
      if (values.documents_owned[key] === true) {
        acc[key] = true;
      }
      return acc;
    }, {});

    // Filter out false values from assets_owned
    const cleanedAssetsOwned = Object.keys(values.assets_owned).reduce((acc, key) => {
      if (values.assets_owned[key] === true) {
        acc[key] = true;
      }
      return acc;
    }, {});

    // Filter out false values from work_mode_choice
    const cleanedWorkModeChoice = Object.keys(values.work_mode_choice).reduce((acc, key) => {
      if (values.work_mode_choice[key] === true) {
        acc[key] = true;
      }
      return acc;
    }, {});

    const formData = {
      phone_number: values.mobile_number,
      user_name: values.user_name,
      email: values.email,
      gender: values.gender === "" ? 0 : values.gender,
      dob: formatDateOfBirth(values.dob),
      pincode: values.pincode,
      state: values.state,
      district: values.district,
      city: values.city,
      current_employment_status: values.current_employment_status === "" ? 0 : values.current_employment_status,
      past_job_count: values.past_job_count || 0,
      past_work_experience: values.past_work_experience === "" ? 0 : values.past_work_experience,
      // english_proficiency: values.english_proficiency === "" ? 0 : values.english_proficiency,
      term_and_conditions: values.term_and_conditions,
      documents_owned: cleanedDocumentsOwned,
      assets_owned: cleanedAssetsOwned,
      work_mode_choice: cleanedWorkModeChoice,
      education_information: educationPayload,
      certification: values.certification.length > 0 ? values.certification.map(cert => cert.value) : [],
      engagement_type: values.engagement_type === "" ? 0 : values.engagement_type,
      expectedSalary: values.expectedSalary === "" ? 0 : values.expectedSalary,
      joining_availability: values.joining_availability || undefined,
      shift_preferences: values.shift_preferences || [],
      known_languages: buildKnownLanguagesPayload(values.language),
      migrate_concern: values.migrate_concern,
    };

    try {
      let res;
      if (editForm) {
        // PUT request for update
        res = await putRequestWithToken(`admin/candidate/update/${values.mobile_number}`, formData);
      } else {
        // POST request for create
        res = await postRequest("admin/candidate/create", formData);
      }

      if (res.data.status === "Success" || res.data.success) {
        const shouldSaveRoleSelection = !editForm || roleSelectionState.isChanged;
        if (shouldSaveRoleSelection) {
          try {
            const candidateId = dataById?._id || await resolveCandidateIdFromResponse(res, values.mobile_number);
            if (!candidateId) {
              throw new Error("Candidate saved, but candidate ID was not available for role selection.");
            }
            await putRequestWithToken(`admin/candidates/${candidateId}/role-selection`, {
              mappingId: roleSelectionState.mappingId,
              familyId: roleSelectionState.familyId,
              groupId: roleSelectionState.groupId,
              roleId: roleSelectionState.roleId,
            });
          } catch (roleErr) {
            setFormSubmitted(false);
            handleSnackBar(true, roleErr?.response?.data?.msg || "Candidate saved, but role selection could not be updated.", "error");
            return;
          }
        }
        try {
          await markAdminOnboardingComplete(values.mobile_number);
        } catch (statusErr) {
          setFormSubmitted(false);
          handleSnackBar(true, statusErr?.response?.data?.msg || "Candidate saved, but onboarding status could not be marked complete.", "error");
          return;
        }
        setFormSubmitted(false);
        handleSnackBar(true, res.data.message || "Operation successful", "success");
        setTimeout(() => {
          navigate("/candidate");
        }, 1000);
        if (!editForm) {
          resetForm();
        }
      } else {
        setFormSubmitted(false);
        handleSnackBar(true, res.data.message || "Operation failed", "error");
      }
    } catch (err) {
      setFormSubmitted(false);
      handleSnackBar(true, err?.response?.data?.message || err.message, "error");
    }
  };

  const fetchPincodeDetails = async (pincode, setFieldValue) => {
    if (!pincode) {
      setFieldValue("district", "");
      setFieldValue("state", "");
      setFieldValue("city", "");
      return;
    }
    try {
      let response = await getRequest(`pincode/${pincode}`);
      const data = response?.data?.data;
      setFieldValue("district", data?.Districtname);
      setFieldValue("state", data?.statename);
      setFieldValue("city", data?.Taluk);
    } catch (err) {
      console.log(err);
    }
  };

  const getCandidateById = async () => {
    try {
      let response = await getRequestWithToken(
        `admin/candidate/${location.state?.mobile}`
      );
      setDataById(response.data.data);
      setShowLoader(false);
    } catch (error) {
      handleSnackBar(
        true,
        error?.response?.data?.msg || error?.message,
        "error"
      );
      setShowLoader(false);
    }
  };

  const checkPermissionFunc = () => {
    const resourceToUpdate = "Candidate";
    const permissions = checkPermission(resourceToUpdate);
    setCanCreate(permissions.canCreate);
    setCanDelete(permissions.canDelete);
    setCanUpdate(permissions.canUpdate);
    setCanRead(permissions.canRead);
  };

  const againCheckPermissions = () => {
    // console.log("hello")
    const checkAccesInArray = localStorage.getItem("resourceNames");
    // console.log("resourceNames : " + checkAccesInArray);
    setisAccess(checkAccesInArray);
    isIncludes();
  };

  const isIncludes = () => {
    setisIncludesValue(isAccess.includes("Candidate"));
  };

  const assets_ownedRequired = [
    { id: 0, label: "None", value: 0 },
    { id: 1, label: "Two Wheeler", value: 1 },
    { id: 2, label: "Four Wheeler", value: 2 },
    { id: 3, label: "Basic Phone", value: 3 },
    { id: 4, label: "Smartphone", value: 4 },
    { id: 5, label: "Own Laptop", value: 5 },
    { id: 6, label: "Internet Connection", value: 6 },
    { id: 7, label: "Tools and Requirement", value: 7 },
    { id: 8, label: "Valid Passport", value: 8 },
  ];

  const governmentDocumentsRequired = [
    { id: 1, label: "Aadhaar Card", value: 1 },
    { id: 2, label: "PAN Card", value: 2 },
    { id: 3, label: "E-Shram Card", value: 3 },
    {
      id: 4,
      label: "Bank Account with passbook",
      value: 4,
    },
    { id: 5, label: "Education Certificate", value: 5 },
    { id: 6, label: "Driving License", value: 6 },
    { id: 7, label: "Valid Passport", value: 7 },
  ];

  const educationQualificationData = [
    // { id: 1, label: "Any", value: 1 },
    { id: 2, label: "10th Pass", value: 2 },
    { id: 3, label: "12th Pass", value: 3 },
    { id: 4, label: "Diploma After 10th", value: 4 },
    { id: 5, label: "Diploma After 12th", value: 5 },
    { id: 6, label: "Graduate", value: 6 },
    { id: 7, label: "Post Graduate", value: 7 },
    { id: 8, label: "ITI", value: 8 },
  ];

  const certificateSpeciliazationData = [
    { id: 0, label: "None", value: 0 },
    { id: 1, label: "ITI", value: 1 },
    { id: 2, label: "DDUGKY", value: 2 },
    { id: 3, label: "Skill India", value: 3 },
  ];

  const englishProficiency = [
    { id: 0, label: "No English", value: 0 },
    // { id: 1, label: "Not Required", value: 1 },
    { id: 2, label: "Thoda English", value: 2 },
    { id: 3, label: "Good English", value: 3 },
    { id: 4, label: "Fluent", value: 4 },
    // { id: 5, label: "NA", value: 5 },
  ];
  const employementStatus = [
    // { id: 0, label: "NA", value: 0 },
    { id: 1, label: "Fresher", value: 1 },
    { id: 2, label: "Unemployed & Experienced", value: 2 },
    { id: 3, label: "Working Full Time", value: 3 },
    { id: 4, label: "Working Part Time", value: 4 },
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 25 }, (_, index) => currentYear - index);

  const getLanguages = async () => {
    try {
      const response = await getRequestWithTokenV3('candidate/languages');
      if (response && response.data && Array.isArray(response.data)) {

        const filteredLanguages = response.data.filter(
          (lang) => lang.label !== "NA"
        );
        console.log("Filtered Languages:", filteredLanguages); // Debug log
        setLanguageOptions(filteredLanguages);
      }
    } catch (err) {
      console.error('Error fetching languages:', err);
      handleSnackBar(true, "Failed to load language options", "error");
    }
  };

  // Initial load effect - runs once on mount
  useEffect(() => {
    checkPermissionFunc();
    againCheckPermissions();
    getLanguages();
  }, []);

  // Update isIncludesValue when isAccess changes
  useEffect(() => {
    if (isAccess) {
      setisIncludesValue(isAccess.includes("Candidate"));
    }
  }, [isAccess]);

  // Fetch candidate data when location state changes
  useEffect(() => {
    if (location.state?.mobile) {
      getCandidateById();
      setVerificationStep('form'); // Skip OTP for edit mode
    } else {
      setShowLoader(false);
      setEditForm(false);
      setVerificationStep('mobile'); // Start with mobile input for create mode
    }
  }, [location.state?.mobile]);

  // Map candidate data to form when dataById changes
  useEffect(() => {
    if (!dataById || Object.keys(dataById).length === 0) return;

    const {
      user_name = "",
      phone_number = "",
      email = "",
      gender = "",
      dob = "",
      pincode = "",
      state = "",
      district = "",
      city = "",
      current_employment_status = "",
      past_job_count = 0,
      past_work_experience = 0,
      english_proficiency = "",
      documents_owned = {},
      assets_owned = {},
      work_mode_choice = {},
      education_information = [],
      certification = [],
      engagement_type = "",
      expectedSalary = 0,
      joining_availability = "",
      shift_preferences = [],
      migrate_concern = false,
      term_and_conditions = true,
      language = []
    } = dataById;

    // Map education_information to form format
    const educationInfo = Array.isArray(education_information) && education_information.length > 0
      ? education_information.map(info => ({
        qualification: info.qualification
      }))
      : [];

    const rawLanguages = dataById.known_languages || language || [];
    const languageArray = Array.isArray(rawLanguages) && rawLanguages.length > 0
      ? rawLanguages.map((item) => {
        const value = item && typeof item === 'object' ? item.value : item;
        const option = languageOptions.find((opt) => String(opt.id) === String(value));
        if (!option && value === undefined) return null;
        return {
          ...(option || { id: String(value), label: String(value), value }),
          value: Number(value),
          speak: item && typeof item === 'object' ? Boolean(item.speak) : true,
          read: item && typeof item === 'object' ? Boolean(item.read) : false,
          write: item && typeof item === 'object' ? Boolean(item.write) : false,
        };
      }).filter(Boolean)
      : [];

    // Map certification - handle both single value and array
    let certificationArray = [];
    if (Array.isArray(certification) && certification.length > 0) {
      // Future case: certification is an array [2, 3]
      certificationArray = certification.map(certId =>
        certificateSpeciliazationData.find(c => c.value === certId)
      ).filter(Boolean);
    } else if (certification !== "" && certification !== 0 && certification !== null && certification !== undefined) {
      // Current case: certification is a single value like 2
      const certItem = certificateSpeciliazationData.find(c => c.value === certification);
      if (certItem) {
        certificationArray = [certItem];
      }
    }

    setInitialValues({
      user_name: user_name || "",
      mobile_number: phone_number || "",
      email: email || "",
      dob: dob ? new Date(dob) : null,
      gender: gender || "",
      pincode: pincode || "",
      state: state || "",
      district: district || "",
      city: city || "",
      current_employment_status: current_employment_status || "",
      past_job_count: past_job_count || 0,
      past_work_experience: Number(current_employment_status) === 1 ? 0 : (past_work_experience || 0),
      english_proficiency: english_proficiency || "",
      term_and_conditions: term_and_conditions !== null ? term_and_conditions : true,
      documents_owned: documents_owned || {},
      assets_owned: assets_owned || {},
      work_mode_choice: work_mode_choice || {},
      education_information: educationInfo,
      certification: certificationArray,
      engagement_type: engagement_type || "",
      expectedSalary: expectedSalary || 0,
      joining_availability: joining_availability || "",
      shift_preferences: Array.isArray(shift_preferences) ? shift_preferences : [],
      migrate_concern: typeof migrate_concern === 'boolean' && migrate_concern !== null ? (migrate_concern === true) : false,
      language: languageArray
    });
    setEditForm(true);
  }, [dataById, languageOptions]);

  // useEffect(() => {
  //   console.log(initialValues.qualification, "qualification");
  //   const getSpecializationEducation = async () => {
  //     console.log(initialValues);
  //     if (!initialValues.qualification.length === 0) return;
  //     let data = await getSpecilizationData("10th pass");
  //     setInitialValues((value) => ({
  //       ...value,
  //       educational_specialisation_data: data,
  //     }));
  //   };
  //   const getSpecializationSkill = async () => {
  //     if (!initialValues.certification_of_vocation_or_skill.trim()) return;
  //     let data = await getSpecilizationData(
  //       initialValues.certification_of_vocation_or_skill
  //     );
  //     setInitialValues((value) => ({ ...value, skillData: data }));
  //   };

  //   getSpecializationEducation();

  // }, [initialValues.qualification]);

  const workModeData = [
    { id: 1, label: "Field Job", value: 1 },
    { id: 2, label: "Work From Office", value: 2 },
    { id: 3, label: "Work From Home", value: 3 },
    { id: 4, label: "Hybrid", value: 4 },
  ];

  const engagementTypeData = [
    // { id: 0, label: "NA", value: 0 },
    { id: 1, label: "Full Time", value: 1 },
    { id: 2, label: "Part Time", value: 2 },
  ];
  const pastworkExp = [
    { id: 0, label: "Fresher", value: 0 },
    { id: 1, label: "<1 year", value: 12 },
    { id: 2, label: "1-3 years", value: 36 },
    { id: 3, label: "3+ years", value: 48 },
  ];

  const expectedSalaryData = [
    { id: 0, label: "₹8K - ₹12K", value: 0 },
    { id: 1, label: "₹12K - ₹18K", value: 1 },
    { id: 2, label: "₹18K - ₹25K", value: 2 },
    { id: 3, label: "₹25K+", value: 3 },
    // { id: 4, label: "NA", value: 4 },
  ];

  const joiningAvailabilityData = [
    { label: "Immediate (within 7 days)", value: "immediate" },
    { label: "15 Days", value: "15_days" },
    { label: "30 Days", value: "30_days" },
  ];

  const shiftPreferenceData = [
    { label: "Day Shift", value: "day_shift" },
    { label: "Night Shift", value: "night_shift" },
    { label: "Rotational Shift", value: "rotational_shift" },
    { label: "Flexible", value: "flexible" },
  ];

  let currentDate = new Date();
  const maxDate = new Date(
    currentDate.setFullYear(currentDate.getFullYear() - 18)
  );

  const physicalFitnessLevel = {
    'Open to Physical Activity': 1,
    '6/6 Vision with Spectacles': 2,
    'No Hearing Impairment': 3,
    'NA': 4
  }

  useEffect(() => {
    const applyHighlight = () => {
      try {
        // highlight native required or aria-required controls
        const selectors = 'input[required], select[required], textarea[required], [aria-required="true"]';
        document.querySelectorAll(selectors).forEach((el) => {
          el.classList.add("jy-required-highlight");
        });

        // find labels with the visual '*' marker and highlight their associated control(s)
        document.querySelectorAll('.must-fill-fields').forEach((star) => {
          const label = star.closest('label');
          if (!label) return;
          const forId = label.getAttribute('for') || label.htmlFor;
          let control = null;
          if (forId) control = document.getElementById(forId);
          if (!control) {
            // try next sibling (common pattern in this form)
            control = label.nextElementSibling;
          }
          if (!control) {
            // fallback: search within the same form-control container
            const container = label.closest('.form-control');
            if (container) {
              control = container.querySelector('input, select, textarea, .MuiInputBase-root, .react-select__control, .MuiAutocomplete-root');
            }
          }
          if (control) {
            control.classList.add('jy-required-highlight');
            // also mark inner select display areas (MUI)
            const innerSelect = control.querySelector && control.querySelector('.MuiSelect-select');
            if (innerSelect) innerSelect.classList.add('jy-required-highlight');
          }
        });
      } catch (e) {
        // silently ignore DOM errors
      }
    };

    // run immediately and shortly after to catch async-rendered controls
    applyHighlight();
    const t = setTimeout(applyHighlight, 600);
    return () => clearTimeout(t);
  }, []);

  // OTP Verification Handlers
  const handleSendOTP = (mobile) => {
    setVerifiedMobileNumber(mobile);
    setVerificationStep('otp');
  };

  const handleOTPVerificationSuccess = () => {
    setVerificationStep('form');
    setInitialValues((prev) => ({
      ...prev,
      mobile_number: verifiedMobileNumber,
    }));
  };

  const handleBackToMobile = () => {
    setVerificationStep('mobile');
    setVerifiedMobileNumber('');
  };

  const handleBackToCandidatesList = () => {
    navigate('/candidate');
  };

  return isIncludesValue ? (
    <>
      {/* Inline styles for required-field highlighting (light green) */}
      <style>{`.jy-required-highlight { background-color: #d4f7d4 !important; }
        .jy-required-highlight input, .jy-required-highlight textarea { background-color: #d4f7d4 !important; }
        .jy-required-highlight .MuiInputBase-root { background-color: #d4f7d4 !important; }
        .jy-required-highlight .MuiSelect-select { background-color: #d4f7d4 !important; }
        .jy-required-highlight .react-select__control { background-color: #d4f7d4 !important; }
        `}</style>
      {showLoader ? (
        <ReusableLoader />
      ) : verificationStep === 'mobile' ? (
        <MobileNumberInput
          onSendOTP={handleSendOTP}
          onBack={handleBackToCandidatesList}
          handleSnackBar={handleSnackBar}
        />
      ) : verificationStep === 'otp' ? (
        <OTPVerification
          mobileNumber={verifiedMobileNumber}
          onVerificationSuccess={handleOTPVerificationSuccess}
          onBack={handleBackToMobile}
          handleSnackBar={handleSnackBar}
        />
      ) : (
        <Box className="create-candidates-section" ref={scrollTargetRef}>
          <Container>
            <Box className="create-candidates">
              <Box className="headingPlusBackBtn">
                <BackBtn />
                <h1 className="formHeadings" id="topEle">
                  {editForm ? "Update Candidate" : "Create Candidate"}
                </h1>
              </Box>
              <Paper
                className="create-candidates-form-wrapper"
                elevation={0}
                sx={{
                  p: { xs: 2, md: 3 },
                  borderRadius: 3,
                  backgroundColor: "transparent",
                  boxShadow: "none",
                }}
              >
                <Stack spacing={3}>
                  <Formik
                    initialValues={initialValues}
                    validationSchema={CreateCandidateValidationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                  >
                    {({ errors, values, setFieldValue, resetForm }) => (
                      <Form className="create-candidates-form">
                        <SectionCard title="Personal Details" subtitle="Primary identity and contact information">
                          <Grid
                            container
                            spacing={2}
                            className="cstmFormGridEmployer"
                          >
                            <Grid item xs={12} sm={6} md={4}>
                              <Box className="form-control">
                                <label
                                  className="form-label"
                                  htmlFor="user_name"
                                >
                                  Full Name{" "}
                                  <span className="must-fill-fields">*</span>
                                </label>
                                <Field
                                  id="user_name"
                                  type="text"
                                  placeholder="Enter Full Name"
                                  name="user_name"
                                  className="form-input"
                                  size="small"
                                />
                                {errors.user_name && (
                                  <Box className="form-error">
                                    {errors.user_name}
                                  </Box>
                                )}
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                              <Box className="form-control">
                                <label
                                  className="form-label"
                                  htmlFor="mobile_number"
                                >
                                  Mobile Number
                                  <span className="must-fill-fields">*</span>
                                </label>
                                <Field
                                  id="mobile_number"
                                  type="text"
                                  placeholder="Enter Mobile Number"
                                  name="mobile_number"
                                  className="form-input"
                                  maxLength={10}
                                  // disabled={editForm}
                                  disabled
                                />
                                {errors.mobile_number && (
                                  <Box className="form-error">
                                    {errors.mobile_number}
                                  </Box>
                                )}
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                              <Box className="form-control">
                                <label className="form-label" htmlFor="email">
                                  Email ID
                                  <span className="must-fill-fields">*</span>
                                </label>
                                <Field
                                  id="email"
                                  type="text"
                                  placeholder="Enter email id"
                                  name="email"
                                  className="form-input"
                                />
                                {errors.email && (
                                  <Box className="form-error">
                                    {errors.email}
                                  </Box>
                                )}
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                              <Box className="form-control">
                                <label className="form-label" htmlFor="gender">
                                  Gender
                                  <span className="must-fill-fields">*</span>
                                </label>
                                <FormControl name="gender" fullWidth size="small">
                                  {values.gender === "" && (
                                    <InputLabel shrink={false}>
                                      Select Gender
                                    </InputLabel>
                                  )}
                                  <Select
                                    placeholder="Select gender"
                                    name="gender"
                                    id="gender"
                                    value={values.gender}
                                    onChange={(event) => {
                                      setFieldValue("gender", event.target.value);
                                    }}
                                  >
                                    <MenuItem value={1}>Female</MenuItem>
                                    <MenuItem value={2}>Male</MenuItem>
                                    <MenuItem value={4}>Other</MenuItem>
                                  </Select>
                                </FormControl>
                                {errors.gender && (
                                  <Box className="form-error">
                                    {errors.gender}
                                  </Box>
                                )}
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                              <Box className="form-control">
                                <label
                                  className="form-label"
                                  htmlFor="dob"
                                >
                                  Date of Birth
                                  <span className="must-fill-fields">*</span>
                                </label>
                                <LocalizationProvider
                                  dateAdapter={AdapterDateFns}
                                >
                                  <DatePicker
                                    className="datePickerGetInput"
                                    value={values.dob}
                                    onChange={(newValue) => {
                                      setFieldValue("dob", newValue);
                                    }}
                                    slotProps={{
                                      textField: {
                                        variant: "outlined",
                                        error: false,
                                        size: "medium",
                                      },
                                    }}
                                    maxDate={maxDate}
                                    format="dd/MM/yyyy"
                                  />
                                </LocalizationProvider>
                                {errors.dob && (
                                  <Box className="form-error">
                                    {errors.dob}
                                  </Box>
                                )}
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                              <Box className="form-control">
                                <label className="form-label" htmlFor="pincode">
                                  Pincode
                                  <span className="must-fill-fields">*</span>
                                </label>
                                <Field
                                  id="pincode"
                                  type="text"
                                  placeholder="Enter pincode"
                                  name="pincode"
                                  className="form-input"
                                  maxLength={6}
                                  onBlur={(event) => fetchPincodeDetails(event.target.value, setFieldValue)}
                                />
                                {errors.pincode && (
                                  <Box className="form-error">
                                    {errors.pincode}
                                  </Box>
                                )}
                              </Box>
                            </Grid>

                            {["state", "district", "city"].map((field) => (
                              <Grid item xs={12} sm={6} md={4} key={field}>
                                <Box className="form-control">
                                  <label className="form-label" htmlFor={field}>{field.charAt(0).toUpperCase() + field.slice(1)}</label>
                                  <Field id={field} type="text" name={field} className="form-input" placeholder={`Enter ${field}`} />
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </SectionCard>

                        <SectionCard title="Employment & Experience" subtitle="Current status, work history, and availability">
                          <Grid
                            container
                            spacing={2}
                            className="cstmFormGridEmployer"
                          >
                            <Grid item xs={12} sm={6} md={4}>
                              <Box className="form-control">
                                <label
                                  className="form-label"
                                  htmlFor="current_employment_status"
                                >
                                  Current Employment Status
                                  <span className="must-fill-fields">*</span>
                                </label>
                                <FormControl fullWidth size="small">
                                  {values.current_employment_status === "" && (
                                    <InputLabel shrink={false}>
                                      Select Employment Status
                                    </InputLabel>
                                  )}
                                  <Select
                                    name="current_employment_status"
                                    id="current_employment_status"
                                    value={values.current_employment_status}
                                    onChange={(event) => {
                                      const nextStatus = event.target.value;
                                      setFieldValue("current_employment_status", nextStatus);
                                      if (Number(nextStatus) === 1) {
                                        setFieldValue("past_work_experience", 0);
                                      }
                                    }}
                                  >
                                    {employementStatus.map((status) => (
                                      <MenuItem key={status.id} value={status.value}>
                                        {status.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                {errors.current_employment_status && (
                                  <Box className="form-error">
                                    {errors.current_employment_status}
                                  </Box>
                                )}
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                              <Box className="form-control">
                                <label
                                  className="form-label"
                                  htmlFor="past_job_count"
                                >
                                  Past Job Count
                                </label>
                                <Field
                                  id="past_job_count"
                                  type="number"
                                  placeholder="Enter number of past jobs"
                                  name="past_job_count"
                                  className="form-input"
                                  min="0"
                                />
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                              <Box className="form-control">
                                <label
                                  className="form-label"
                                  htmlFor="past_work_experience"
                                >
                                  Past Work Experience
                                  <span className="must-fill-fields">*</span>
                                </label>
                                <FormControl fullWidth size="small">
                                  {values.past_work_experience === "" && (
                                    <InputLabel shrink={false}>
                                      Select Experience
                                    </InputLabel>
                                  )}
                                  <Select
                                    name="past_work_experience"
                                    id="past_work_experience"
                                    value={values.past_work_experience}
                                    disabled={Number(values.current_employment_status) === 1}
                                    onChange={(event) => {
                                      setFieldValue("past_work_experience", event.target.value);
                                    }}
                                  >
                                    {pastworkExp.map((exp) => (
                                      <MenuItem key={exp.id} value={exp.value}>
                                        {exp.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                {errors.past_work_experience && (
                                  <Box className="form-error">
                                    {errors.past_work_experience}
                                  </Box>
                                )}
                                {Number(values.current_employment_status) === 1 && (
                                  <Typography variant="caption" color="text.secondary">
                                    Fresher candidates have no past work experience.
                                  </Typography>
                                )}
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                              <Box className="form-control">
                                <label className="form-label" htmlFor="joining_availability">Joining Availability<span className="must-fill-fields">*</span></label>
                                <FormControl fullWidth size="small">
                                  <Select name="joining_availability" id="joining_availability" value={values.joining_availability} displayEmpty onChange={(event) => setFieldValue("joining_availability", event.target.value)}>
                                    <MenuItem value=""><em>Select Availability</em></MenuItem>
                                    {joiningAvailabilityData.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                                  </Select>
                                </FormControl>
                                {errors.joining_availability && (
                                  <Box className="form-error">
                                    {errors.joining_availability}
                                  </Box>
                                )}
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6} md={8}>
                              <Box className="form-control">
                                <label className="form-label">Shift Preferences<span className="must-fill-fields">*</span></label>
                                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                  {shiftPreferenceData.map((shift) => (
                                    <label key={shift.value}>
                                      <Field
                                        type="checkbox"
                                        name="shift_preferences"
                                        checked={values.shift_preferences.includes(shift.value)}
                                        onChange={(event) => {
                                          const next = event.target.checked
                                            ? [...values.shift_preferences, shift.value]
                                            : values.shift_preferences.filter((value) => value !== shift.value);
                                          setFieldValue("shift_preferences", next);
                                        }}
                                      />
                                      {" "}{shift.label}
                                    </label>
                                  ))}
                                </Stack>
                                {errors.shift_preferences && (
                                  <Box className="form-error">
                                    {errors.shift_preferences}
                                  </Box>
                                )}
                              </Box>
                            </Grid>

                            {/* <Grid item xs={12} sm={6} md={4}>
                            <Box className="form-control">
                              <label
                                className="form-label"
                                htmlFor="english_proficiency"
                              >
                                English Proficiency
                              </label>
                              <FormControl fullWidth size="small">
                                {values.english_proficiency === "" && (
                                  <InputLabel shrink={false}>
                                    Select English Level
                                  </InputLabel>
                                )}
                                <Select
                                  name="english_proficiency"
                                  id="english_proficiency"
                                  value={values.english_proficiency}
                                  onChange={(event) => {
                                    setFieldValue("english_proficiency", event.target.value);
                                  }}
                                >
                                  {englishProficiency.map((level) => (
                                    <MenuItem key={level.id} value={level.value}>
                                      {level.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          </Grid> */}

                            <Grid item xs={12}>
                              <Box className="form-control">
                                <label
                                  className="form-label"
                                  htmlFor="language"
                                >
                                  Known Languages
                                  <span className="must-fill-fields">*</span>
                                </label>
                                <Autocomplete
                                  multiple
                                  id="language"
                                  options={languageOptions}
                                  getOptionLabel={(option) => option.label || ""}
                                  value={values.language}
                                  onChange={(event, newValue) => {
                                    const nextValue = newValue.map((lang) => {
                                      const existing = values.language.find((item) => String(item.id ?? item.value) === String(lang.id ?? lang.value));
                                      return {
                                        ...lang,
                                        value: Number(lang.value ?? lang.id),
                                        speak: existing?.speak ?? true,
                                        read: existing?.read ?? false,
                                        write: existing?.write ?? false,
                                      };
                                    });
                                    setFieldValue("language", nextValue);
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select languages"
                                      error={!!errors.language}
                                    />
                                  )}
                                />
                                {errors.language && (
                                  <Box className="form-error">
                                    {errors.language}
                                  </Box>
                                )}
                                {values.language.length > 0 && (
                                  <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                                    {values.language.map((lang, index) => (
                                      <Box key={lang.id ?? lang.value} sx={{ p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{lang.label}</Typography>
                                        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                                          {["speak", "read", "write"].map((skill) => (
                                            <label key={skill} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                              <Checkbox
                                                size="small"
                                                checked={Boolean(values.language[index]?.[skill])}
                                                onChange={(event) => {
                                                  const nextLanguages = values.language.map((item, itemIndex) => itemIndex === index ? { ...item, [skill]: event.target.checked } : item);
                                                  setFieldValue("language", nextLanguages);
                                                }}
                                              />
                                              {skill.charAt(0).toUpperCase() + skill.slice(1)}
                                            </label>
                                          ))}
                                        </Stack>
                                      </Box>
                                    ))}
                                  </Stack>
                                )}
                              </Box>
                            </Grid>
                          </Grid>
                        </SectionCard>

                        <SectionCard title="Job Preferences" subtitle="Job intent">
                          <Grid
                            container
                            spacing={2}
                            className="cstmFormGridEmployer"
                          >
                            <Grid item xs={12} sm={6} md={6}>
                              <Box className="form-control">
                                <label className="form-label">
                                  Work Mode Preference
                                </label>
                                <Box>
                                  {workModeData.map((mode) => (
                                    <Box key={mode.id} style={{ display: "inline-block", marginRight: "15px" }}>
                                      <label>
                                        <Field
                                          type="checkbox"
                                          name={`work_mode_choice.${mode.id}`}
                                          checked={values.work_mode_choice[mode.id] || false}
                                          onChange={(e) => {
                                            setFieldValue(`work_mode_choice.${mode.id}`, e.target.checked);
                                          }}
                                        />
                                        {" "}{mode.label}
                                      </label>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <Box className="form-control">
                                <label
                                  className="form-label"
                                  htmlFor="engagement_type"
                                >
                                  Engagement Type
                                </label>
                                <FormControl fullWidth size="small">
                                  {values.engagement_type === "" && (
                                    <InputLabel shrink={false}>
                                      Select Type
                                    </InputLabel>
                                  )}
                                  <Select
                                    name="engagement_type"
                                    id="engagement_type"
                                    value={values.engagement_type}
                                    onChange={(event) => {
                                      setFieldValue("engagement_type", event.target.value);
                                    }}
                                  >
                                    {engagementTypeData.map((type) => (
                                      <MenuItem key={type.id} value={type.value}>
                                        {type.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <Box className="form-control">
                                <label
                                  className="form-label"
                                  htmlFor="expectedSalary"
                                >
                                  Expected Salary
                                  <span className="must-fill-fields">*</span>
                                </label>
                                <FormControl fullWidth size="small">
                                  {values.expectedSalary === "" && (
                                    <InputLabel shrink={false}>
                                      Select Range
                                    </InputLabel>
                                  )}
                                  <Select
                                    name="expectedSalary"
                                    id="expectedSalary"
                                    value={values.expectedSalary}
                                    onChange={(event) => {
                                      setFieldValue("expectedSalary", event.target.value);
                                    }}
                                  >
                                    {expectedSalaryData.map((salary) => (
                                      <MenuItem key={salary.id} value={salary.value}>
                                        {salary.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                {errors.expectedSalary && (
                                  <Box className="form-error">
                                    {errors.expectedSalary}
                                  </Box>
                                )}
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                              <Box className="form-control">
                                <label className="form-label">
                                  Do you want jobs only in this city or in other cities too?
                                  <span className="must-fill-fields">*</span>
                                </label>
                                <Box>
                                  <Box style={{ marginBottom: "8px" }}>
                                    <label>
                                      <Field
                                        type="radio"
                                        name="migrate_concern"
                                        value="false"
                                        checked={values.migrate_concern === false}
                                        onChange={() => {
                                          setFieldValue("migrate_concern", false);
                                        }}
                                      />
                                      {" "}Only in my city
                                    </label>
                                  </Box>
                                  <Box>
                                    <label>
                                      <Field
                                        type="radio"
                                        name="migrate_concern"
                                        value="true"
                                        checked={values.migrate_concern === true}
                                        onChange={() => {
                                          setFieldValue("migrate_concern", true);
                                        }}
                                      />
                                      {" "}Other cities also
                                    </label>
                                  </Box>
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                        </SectionCard>

                        <SectionCard title="Qualifications" subtitle="Education and certifications">
                          <Grid
                            container
                            spacing={2}
                            className="cstmFormGridEmployer"
                          >
                            <Grid item xs={12} sm={6} md={6}>
                              <Box className="form-control">
                                <label
                                  className="form-label"
                                  htmlFor="education_information"
                                >
                                  Educational Qualifications
                                  <span className="must-fill-fields">*</span>
                                </label>
                                <Autocomplete
                                  multiple
                                  id="education_information"
                                  options={educationQualificationData}
                                  getOptionLabel={(option) => option.label}
                                  value={values.education_information.map(ed =>
                                    educationQualificationData.find(q => q.id === ed.qualification)
                                  ).filter(Boolean)}
                                  onChange={(event, newValue) => {
                                    setFieldValue("education_information",
                                      newValue.map(v => ({ qualification: v.id }))
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select qualifications"
                                    />
                                  )}
                                />
                                {errors.education_information && (
                                  <Box className="form-error">
                                    {errors.education_information}
                                  </Box>
                                )}
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6} md={6}>
                              <Box className="form-control">
                                <label
                                  className="form-label"
                                  htmlFor="certification"
                                >
                                  Vocational Certification
                                </label>
                                <Autocomplete
                                  multiple
                                  id="certification"
                                  options={certificateSpeciliazationData}
                                  getOptionLabel={(option) => option.label}
                                  value={values.certification}
                                  onChange={(event, newValue) => {
                                    setFieldValue("certification", newValue);
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select certifications"
                                    />
                                  )}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        </SectionCard>

                        <SectionCard title="Assets & Documents" subtitle="Verification documents and available assets">
                          <Grid
                            container
                            spacing={2}
                            className="cstmFormGridEmployer"
                          >
                            <Grid item xs={12} sm={6} md={6}>
                              <Box className="form-control">
                                <label className="form-label">
                                  Documents Owned
                                  <span className="must-fill-fields">*</span>
                                </label>
                                <Box>
                                  {governmentDocumentsRequired.map((doc) => (
                                    <Box key={doc.id} style={{ marginBottom: "8px" }}>
                                      <label>
                                        <Field
                                          type="checkbox"
                                          name={`documents_owned.${doc.id}`}
                                          checked={values.documents_owned[doc.id] || false}
                                          onChange={(e) => {
                                            setFieldValue(`documents_owned.${doc.id}`, e.target.checked);
                                          }}
                                        />
                                        {" "}{doc.label}
                                      </label>
                                    </Box>
                                  ))}
                                </Box>
                                {errors.documents_owned && (
                                  <Box className="form-error">
                                    {errors.documents_owned}
                                  </Box>
                                )}
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6} md={6}>
                              <Box className="form-control">
                                <label className="form-label">
                                  Assets Owned
                                  <span className="must-fill-fields">*</span>
                                </label>
                                <Box>
                                  {assets_ownedRequired.map((asset) => (
                                    <Box key={asset.id} style={{ marginBottom: "8px" }}>
                                      <label>
                                        <Field
                                          type="checkbox"
                                          name={`assets_owned.${asset.id}`}
                                          checked={values.assets_owned[asset.id] || false}
                                          onChange={(e) => {
                                            if (asset.id === 0 && e.target.checked) {
                                              setFieldValue("assets_owned", { 0: true });
                                              return;
                                            }

                                            if (asset.id !== 0 && e.target.checked) {
                                              setFieldValue("assets_owned", {
                                                ...values.assets_owned,
                                                0: false,
                                                [asset.id]: true,
                                              });
                                              return;
                                            }

                                            setFieldValue(`assets_owned.${asset.id}`, e.target.checked);
                                          }}
                                        />
                                        {" "}{asset.label}
                                      </label>
                                    </Box>
                                  ))}
                                </Box>
                                {errors.assets_owned && (
                                  <Box className="form-error">
                                    {errors.assets_owned}
                                  </Box>
                                )}
                              </Box>
                            </Grid>
                          </Grid>
                        </SectionCard>

                        <SectionCard title="Role Selection" subtitle="Required. Family, group, and role are saved with the candidate form.">
                          <CandidateRoleSelectionPanel
                            candidateId={editForm ? dataById?._id : null}
                            onSelectionChange={(selection) => {
                              setRoleSelectionState(selection);
                              if (selection.mappingId) setRoleSelectionError("");
                            }}
                            showSave={false}
                          />
                          {roleSelectionError && (
                            <Box className="form-error" sx={{ mt: 1 }}>
                              {roleSelectionError}
                            </Box>
                          )}
                        </SectionCard>

                        {/* <Box className="backgroundStyle">
                        <Grid
                          container
                          spacing={2}
                          className="cstmFormGridEmployer"
                        >
                          <Grid item xs={12}>
                            <Box className="form-control">
                              <label>
                                <Field
                                  type="checkbox"
                                  name="term_and_conditions"
                                  checked={values.term_and_conditions}
                                  onChange={(e) => {
                                    setFieldValue("term_and_conditions", e.target.checked);
                                  }}
                                />
                                {" "}I agree to the terms and conditions
                              </label>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box> */}

                        <Box style={{ padding: "20px", display: "flex", gap: "15px" }}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              if (!roleSelectionState.mappingId) {
                                setRoleSelectionError("Family, group, and role are required.");
                              }
                            }}
                            disabled={formSubmitted}
                            style={{ minWidth: "200px", minHeight: "50px" }}
                          >
                            {formSubmitted ? <ReusableLoader /> : editForm ? "Update Candidate" : "Create Candidate"}
                          </Button>
                          <Button
                            type="button"
                            variant="outlined"
                            color="secondary"
                            onClick={() => navigate("/candidate")}
                            disabled={formSubmitted}
                            style={{ minWidth: "200px", minHeight: "50px" }}
                          >
                            Cancel
                          </Button>
                        </Box>
                        <FocusError handleSnackBar={handleSnackBar} />
                      </Form>
                    )}
                  </Formik>
                </Stack>
              </Paper>
            </Box>
          </Container>
        </Box>
      )}
    </>
  ) : (
    <Unauthorized />
  );
};
export default CreateCandidates;